import { Injectable } from '@angular/core';
import { Headers, Http, Response } from '@angular/http';
import { Router } from '@angular/router';
import { StringifyOptions } from 'querystring';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { inflate } from 'zlib';

import { environment } from '../../environments/environment';
import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';

import { allTemplateFields } from '../../ngrx/graphql';

import { Template } from '../models';

const errorCodes = [400, 401, 403, 404];

@Injectable()
export class TemplateService {
  private bucketName: string = environment.imposerTemplatesBucket;

  constructor(
    private authService      : AuthenticationService,
    private credentialService: CredentialService,
    private router           : Router,
    private http             : Http,
    private sanitizer        : DomSanitizer
  ) {}

  private invokeTemplates(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.templateFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                :       data.Payload;
              if(output.gzip){
                const options = {
                  windowBits : 15
                };
                inflate(new Buffer(output.body,'base64'), options, (err, data) => {
                  if (err) {                    
                    observer.error(err.stack);
                  } else {
                    const unzippedOutput = Object.assign({}, output, {
                      body: data.toString()
                    });
                    observer.next(unzippedOutput);
                  }
                });
              } else {
                observer.next(output);
              }
          }
        }
      );
    });
  }

  private invokeTemplateMutation(payload: any): void {
    this.credentialService.lambda.invoke({
      FunctionName: environment.templateFunction,
      Payload: JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
            // this.authService.logout();
          }
        }
      }
    );
  }

  createTemplate(template: Template): void {
    const variables = { template };
    const query = `mutation($template: TemplateCreateInput!) {
      createTemplate(template: $template) {
        ${allTemplateFields}
      }
    }`;

    this.invokeTemplateMutation({ query, variables });
  }

  deleteTemplate(id: string): void {
    const variables = { id };
    const query = `mutation($id: ID!) {
      deleteTemplate(id: $id) {
        id
      }
    }`;

    this.invokeTemplateMutation({ query, variables });
  }

  getTemplate(id: string, query?: string): Observable<any> {
    const returnAllFields = `{
      template(id: "${id}") {
        ${allTemplateFields}
      }
    }`;

    return this.invokeTemplates({ query: !query ? returnAllFields : query });
  }

  getTemplates(): Observable<any> {
    const query = `{
      templates(param: "all") {
        ${allTemplateFields}
      }
    }`;

    return this.invokeTemplates({ query });
  }

  updateTemplate(template: Template): void {
    console.log(`updating template: ${JSON.stringify(template, null, 2)}`);
    if (template) {
      const variables = { template };
      const query = `mutation($template: TemplateInput!) {
        updateTemplate(template: $template) {
          ${allTemplateFields}
        }
      }`;

      this.invokeTemplateMutation({ query, variables });
    }
  }

  updateTemplates(templates: Template[]): void {
    console.log(`updating templates: ${JSON.stringify(templates, null, 2)}`);
    if (templates && templates.length) {
      const variables = { templates };
      const query = `mutation($templates: [TemplateInput]) {
        updateTemplates(templates: $templates) {
          ${allTemplateFields}
        }
      }`;

      this.invokeTemplateMutation({ query, variables });
    }
  }

  getTemplatePDFList(): Observable<any> {
    return new Observable(observer => {

      this.credentialService.s3.listObjectsV2({
        Bucket: this.bucketName
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
            // this.authService.logout();
          }
          observer.error(err.stack);
        } else {
          Promise.all(data.Contents.map(async (pdf) => {
            const pdfKey = pdf.Key;
            const pdfUrl = await this.credentialService.s3.getSignedUrl('getObject', {
              Bucket: this.bucketName,
              Key: pdfKey
            });

            return {
              key: pdfKey,
              url: this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl)
            };
          }))
            .then(pdfs => observer.next(pdfs));
        }
      });
    });
  }

  getTemplatePDF(key: string): Observable<any> {
    return new Observable(observer => {

      this.credentialService.s3.getSignedUrl('getObject', {
        Bucket: this.bucketName,
        Key: key
      }, (err: any, url) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
            // this.authService.logout();
          }
          observer.error(err);
        }

        observer.next({
          key,
          url: this.sanitizer.bypassSecurityTrustResourceUrl(url)
        });
      });
    });
  }

  addTemplatePDF(file: File, duplicates: number): Observable<any> {
    return new Observable(observer => {

      const key = duplicates ? `${file.name}[${duplicates}]` : file.name;

      this.credentialService.s3.putObject({
        Bucket: this.bucketName,
        Key: key,
        Body: file,
        ContentType: 'application/pdf'
      }, async (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
            // this.authService.logout();
          }
          observer.error(err.stack);
        } else {
          const pdfUrl = await this.credentialService.s3.getSignedUrl('getObject', {
            Bucket: this.bucketName,
            Key: key
          });

          observer.next({ key, url: this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl) });
        }
      });
    });
  }

  deleteTemplatePDF(key: string): Observable<any> {
    return new Observable(observer => {

      this.credentialService.s3.deleteObject({
        Bucket: this.bucketName,
        Key: key
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
            // this.authService.logout();
          }
          observer.error(err);
        } else {
          observer.next(key);
        }
      });
    });
  }

  invokeGenerateImposedPDF(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.imposerPdfFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                :       data.Payload;
              if(output.gzip){
                const options = {
                  windowBits : 15
                };
                inflate(new Buffer(output.body,'base64'), options, (err, data) => {
                  if (err) {                    
                    observer.error(err.stack);
                  } else {
                    const unzippedOutput = Object.assign({}, output, {
                      body: data.toString()
                    });
                    observer.next(unzippedOutput);
                  }
                });
              } else {
                observer.next(output);
              }
          }
        }
      );
    });
  }
}


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/services/template.service.ts